import type { Object3D, WebGLRenderer } from 'three';
import { CARD_DEPTH, CARD_WIDTH } from './card-mesh.js';
import { finishAllTweens } from './anim.js';

interface CascadeCard {
  group: Object3D;
  vx: number;
  vy: number;
  vz: number;
  vrx: number;
  vry: number;
  vrz: number;
  bounces: number;
  settled: boolean;
}

export interface WinCascadeOptions {
  renderer: WebGLRenderer;
  onComplete(): void;
}

export interface WinCascade {
  start(groups: Object3D[], center?: { x: number; z: number }): void;
  cancel(): void;
  update(dt: number): void;
  isActive(): boolean;
}

export function createWinCascade(opts: WinCascadeOptions): WinCascade {
  let active = false;
  let startedAt = 0;
  const cards: CascadeCard[] = [];

  return {
    start(groups, center = { x: 0, z: 0 }) {
      active = true;
      startedAt = performance.now();
      cards.length = 0;
      finishAllTweens();
      for (const group of groups) {
        // Aim each card toward the play-area center so the burst converges on screen
        // before falling - instead of just spraying outward from one corner.
        const p = group.position;
        const dx = center.x - p.x;
        const dz = center.z - p.z;
        const dist = Math.hypot(dx, dz);
        const aimX = dist > 0.05 ? dx / dist : 0;
        const aimZ = dist > 0.05 ? dz / dist : 0;
        cards.push({
          group,
          vx: aimX * 3 + (Math.random() - 0.5) * 2.5,
          vy: 4 + Math.random() * 3,
          vz: aimZ * 3 + (Math.random() - 0.5) * 2.5,
          vrx: (Math.random() - 0.5) * 4,
          vry: (Math.random() - 0.5) * 4,
          vrz: (Math.random() - 0.5) * 6,
          bounces: 0,
          settled: false,
        });
      }
    },
    cancel() {
      active = false;
      cards.length = 0;
    },
    isActive() {
      return active;
    },
    update(dt) {
      if (!active) return;
      const tableY = CARD_DEPTH / 2;
      const gravity = 9.8;
      let allDone = true;
      for (const c of cards) {
        if (c.settled) continue;
        const p = c.group.position;
        p.x += c.vx * dt;
        p.y += c.vy * dt;
        p.z += c.vz * dt;
        c.group.rotation.x += c.vrx * dt;
        c.group.rotation.y += c.vry * dt;
        c.group.rotation.z += c.vrz * dt;
        c.vy -= gravity * dt;
        if (p.y <= tableY && c.vy < 0) {
          p.y = tableY;
          if (c.bounces < 3) {
            c.vy = -c.vy * 0.5;
            c.vx *= 0.8;
            c.vz *= 0.8;
            c.vrx *= 0.6;
            c.vry *= 0.6;
            c.vrz *= 0.6;
            c.bounces++;
          } else {
            // Settle: lay flat on the table, frozen. Beats falling through the
            // floor or bouncing forever at hairline velocities.
            c.settled = true;
            c.vx = c.vy = c.vz = 0;
            c.vrx = c.vry = c.vrz = 0;
          }
        }
        if (!c.settled) allDone = false;
      }

      // Sphere-sphere collision pass: stop cards from intersecting in the air.
      const radius = CARD_WIDTH * 0.45;
      const minDist = radius * 2;
      const minDist2 = minDist * minDist;
      for (let i = 0; i < cards.length; i++) {
        const ai = cards[i]!;
        if (ai.settled) continue;
        const a = ai.group.position;
        for (let j = i + 1; j < cards.length; j++) {
          const bj = cards[j]!;
          if (bj.settled) continue;
          const b = bj.group.position;
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const dz = b.z - a.z;
          const d2 = dx * dx + dy * dy + dz * dz;
          if (d2 >= minDist2 || d2 < 1e-6) continue;
          const d = Math.sqrt(d2);
          const nx = dx / d;
          const ny = dy / d;
          const nz = dz / d;
          const overlap = (minDist - d) * 0.5;
          a.x -= nx * overlap; a.y -= ny * overlap; a.z -= nz * overlap;
          b.x += nx * overlap; b.y += ny * overlap; b.z += nz * overlap;
          const va = ai.vx * nx + ai.vy * ny + ai.vz * nz;
          const vb = bj.vx * nx + bj.vy * ny + bj.vz * nz;
          if (va - vb > 0) continue;
          const restitution = 0.6;
          const impulse = (vb - va) * (1 + restitution) * 0.5;
          ai.vx += impulse * nx; ai.vy += impulse * ny; ai.vz += impulse * nz;
          bj.vx -= impulse * nx; bj.vy -= impulse * ny; bj.vz -= impulse * nz;
        }
      }

      if (allDone || performance.now() - startedAt > 7000) {
        active = false;
        opts.onComplete();
      }
      opts.renderer.shadowMap.needsUpdate = true;
    },
  };
}
